import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent
} from "react";
import { Box, CircularProgress, IconButton, Menu, MenuItem, Slider, Tooltip, Typography } from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import PauseIcon from "@mui/icons-material/Pause";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import videojs from "video.js";
import LoadingDialog from "@/components/ui/LoadingDialog";
import { useVideoEntity, useUploadImage } from "@/api/api";
import { toastError, toastSuccess } from "@/utils/toast";
import { captureFrameFilename, formatMediaTimeSeconds, formatPlaybackRateLabel, inferVideoMimeTypeFromUrl } from "@/utils/utils";
import { MIN_TRIM_GAP_SEC } from "@/utils/constants";

export interface VideoPlayerProps {
  /**
   * Presigned playback URL. When set, the player does not call the video entity API.
   */
  readonly src?: string | null;
  /** When `src` is not provided, fetches URL via GET /{projectId}/video/{videoId}. */
  readonly projectId?: string | null;
  readonly videoId?: string | null;
  /** Gate internal fetch (e.g. only while a dialog is open). Ignored when `src` is set. */
  readonly fetchEnabled?: boolean;
  readonly poster?: string | undefined;
  readonly autoPlay?: boolean;
  readonly playsInline?: boolean;
  readonly disablePictureInPicture?: boolean;
  /** Applied to the video.js tech wrapper area (sizing / `objectFit` feel). */
  readonly sx?: SxProps<Theme>;
  /** Outer wrapper (e.g. alignment in flex layouts). */
  readonly containerSx?: SxProps<Theme>;
  /** Receives the uploaded project image id after imperative `captureFrame()` succeeds. */
  readonly onCaptureFrame?: (imageId: string) => void;
  /**
   * MIME type for Video.js (e.g. `video/mp4`, `application/x-mpegURL`). Use when the URL has no extension.
   */
  readonly sourceMimeType?: string;
  /**
   * Size the player height to the video aspect ratio at the current width (no huge letterboxed band).
   * Use in dialogs; pair with `containerSx={{ flex: '0 0 auto', width: '100%' }}`.
   */
  readonly fitContentHeight?: boolean;
  /**
   * When `fitContentHeight`, cap video host height to this many CSS pixels (e.g. measured dialog body).
   * Avoids clipping when the dialog slot is shorter than `window.innerHeight`.
   */
  readonly fitMaxHeightPx?: number;
  /**
   * Draggable in/out “curtains” on the picture: darkened regions outside the trim range; playback stays within [start, end].
   */
  readonly showTrimCurtains?: boolean;
  /** Fires when trim changes (pointer release after drag, or duration sets default end). */
  readonly onTrimRangeChange?: (range: { readonly startSec: number; readonly endSec: number }) => void;
  /** Seeds the curtain positions once duration is known (e.g. restoring a previously-set trim). Ignored after the initial seed. */
  readonly initialTrimRange?: { readonly startSec: number; readonly endSec: number } | null;
  /**
   * Clamp playback to [startSec, endSec] without trim curtains (e.g. playlist preview).
   * When set, overrides curtain-based trim for playback bounds only.
   */
  readonly playbackClamp?: { readonly startSec: number; readonly endSec: number } | null;
  /** Fired once when playback crosses the clamp end while playing (see `playbackClamp`). */
  readonly onReachedPlaybackClampEnd?: () => void;
  /** Fired when playback reaches the end of the source with no `playbackClamp` set (e.g. sequence auto-advance). */
  readonly onEnded?: () => void;
  /** Fired when Video.js fails to decode/load the current source (e.g. expired presigned URL). */
  readonly onPlaybackError?: () => void;
  /**
   * When false, omit Video.js `crossOrigin` (presigned S3 preview in dialogs).
   * Default true — required for canvas grab-frame in Main Space.
   */
  readonly enableCrossOrigin?: boolean;
}

/** Imperative capture used by parent controls such as MainSpace's “Grab frame” action. */
export interface VideoPlayerHandle {
  readonly captureFrame: () => Promise<void>;
  /** Current trim window (curtain UI), or null if duration unknown. */
  readonly getTrimRange: () => { readonly startSec: number; readonly endSec: number } | null;
}

type VideoJsPlayer = ReturnType<typeof videojs>;

/** Video.js plugin: ensure the built-in Big Play overlay is present (works with `controlBar: false`). */
const BIG_PLAY_BUTTON_PLUGIN = "videoPlayerBigPlayButton";

if (typeof videojs.getPlugin === "function" && videojs.getPlugin(BIG_PLAY_BUTTON_PLUGIN) === undefined) {
  videojs.registerPlugin(BIG_PLAY_BUTTON_PLUGIN, function (this: VideoJsPlayer) {
    this.ready(() => {
      if (!this.getChild("bigPlayButton")) {
        this.addChild("BigPlayButton");
      }

      const el = this.getChild("bigPlayButton")?.el();
      if (el instanceof HTMLElement) {
        el.setAttribute("aria-label", "Play video");
      }
    });
  });
}

function getHtmlVideo(player: VideoJsPlayer | null): HTMLVideoElement | null {
  if (!player) {
    return null;
  }

  const el = player.el()?.querySelector("video");
  return el instanceof HTMLVideoElement ? el : null;
}

/** `data-vjs-player` host — always the parent of our `<video>` in this layout (avoids a separate wrapper ref). */
function getVideoIngestHost(video: HTMLVideoElement | null): HTMLDivElement | null {
  if (!video) {
    return null;
  }

  return video.parentElement instanceof HTMLDivElement ? video.parentElement : null;
}

/** Matches the in-player glass control strip style on [videojs.org](https://videojs.org/). */
const PLAYBACK_RATES = [0.5, 0.75, 1, 1.25, 1.5, 2] as const;

/** CSS gradient painting a primary-color "kept" segment (`startPct`–`endPct`) over an otherwise dim rail. */
function trimRailHighlightGradient(startPct: number, endPct: number): string {
  const dim = "rgba(255,255,255,0.25)";
  const kept = "rgba(30,136,229,0.75)"; // theme.palette.primary.main
  const stops = [
    `${dim} 0%`,
    `${dim} ${startPct}%`,
    `${kept} ${startPct}%`,
    `${kept} ${endPct}%`,
    `${dim} ${endPct}%`,
    `${dim} 100%`
  ];
  return `linear-gradient(to right, ${stops.join(", ")})`;
}

/**
 * Video.js's own `MediaError` codes (mirrors the DOM `MediaError` constants). Only `NETWORK`
 * means the underlying byte-range/segment request itself failed - e.g. a transient S3/CDN 5xx
 * mid-playback (see `iac/MEDIA_CDN_PROPOSAL.md`) - which is worth retrying. `SRC_NOT_SUPPORTED` /
 * `DECODE` are not: re-requesting the exact same URL will fail identically every time.
 */
const MEDIA_ERR_NETWORK = 2;
const PLAYBACK_RETRY_MAX_ATTEMPTS = 3;
const PLAYBACK_RETRY_BASE_DELAY_MS = 800;

/**
 * Shared video playback via **video.js** with an **in-player** overlay control bar
 * (play, seek, time, volume, playback rate, fullscreen).
 */
const VideoPlayer = forwardRef<VideoPlayerHandle, VideoPlayerProps>(function (
  {
    src: srcProp,
    projectId,
    videoId,
    fetchEnabled = true,
    poster,
    autoPlay = false,
    playsInline = true,
    disablePictureInPicture = true,
    sx,
    containerSx,
    onCaptureFrame,
    sourceMimeType,
    fitContentHeight = false,
    fitMaxHeightPx,
    showTrimCurtains = false,
    onTrimRangeChange,
    initialTrimRange = null,
    playbackClamp = null,
    onReachedPlaybackClampEnd,
    onEnded,
    onPlaybackError,
    enableCrossOrigin = true
  },
  ref
) {
  /** `<video>` node passed to Video.js */
  const videoElRef = useRef<HTMLVideoElement | null>(null);
  /** Video.js player instance (not a DOM ref) */
  const playerRef = useRef<VideoJsPlayer | null>(null);
  /** Fullscreen + outer layout root */
  const rootRef = useRef<HTMLDivElement | null>(null);
  /** Off-screen bitmap for Grab frame */
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [muted, setMuted] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [rateMenuAnchor, setRateMenuAnchor] = useState<HTMLElement | null>(null);
  /** Letterboxed picture width inside the host; control strip matches this (centered). */
  const [controlBarWidthPx, setControlBarWidthPx] = useState<number | null>(null);
  /** `rootRef` is the fullscreen target; sync for tooltip / aria. */
  const [isPlayerFullscreen, setIsPlayerFullscreen] = useState(false);
  /** When `fitContentHeight`, pixel height of the video host from intrinsic aspect ratio. */
  const [fitHostHeightPx, setFitHostHeightPx] = useState<number | null>(null);
  /** True from start of Grab frame (canvas + upload) until finished — drives {@link LoadingDialog}. */
  const [captureFrameLoading, setCaptureFrameLoading] = useState(false);

  const hookSafeDuration = Number.isFinite(duration) && duration > 0 ? duration : 0;
  const [trimStartSec, setTrimStartSec] = useState(0);
  const [trimEndSec, setTrimEndSec] = useState(0);
  /** Parent only updates this on drag commit, so syncing on change doesn't fight an in-progress drag. */
  const seedTrimStartSec = initialTrimRange?.startSec ?? null;
  const seedTrimEndSec = initialTrimRange?.endSec ?? null;
  /** Latest trim for playback clamp (read inside Video.js `timeupdate`; avoids stale closures). */
  const trimPlaybackRef = useRef({ enabled: false, start: 0, end: 0 });
  const trimStartRef = useRef(0);
  const trimEndRef = useRef(0);
  /** Playhead time before a trim handle drag started; the handle previews its frame, then the playhead returns here. */
  const preTrimDragTimeRef = useRef<number | null>(null);
  const playbackClampRef = useRef(playbackClamp);
  playbackClampRef.current = playbackClamp;
  const onReachedPlaybackClampEndRef = useRef(onReachedPlaybackClampEnd);
  onReachedPlaybackClampEndRef.current = onReachedPlaybackClampEnd;
  const onEndedRef = useRef(onEnded);
  onEndedRef.current = onEnded;
  const onPlaybackErrorRef = useRef(onPlaybackError);
  onPlaybackErrorRef.current = onPlaybackError;
  /** Avoid repeated `onReachedPlaybackClampEnd` while paused on end. */
  const segmentEndNotifiedRef = useRef(false);
  /** Count of in-flight network-error retries for the current source (reset on `effectiveSrc` change / successful playback). */
  const playbackRetryCountRef = useRef(0);
  const playbackRetryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (hookSafeDuration <= 0) {
      setTrimStartSec(0);
      setTrimEndSec(0);
      return;
    }

    if (seedTrimStartSec !== null && seedTrimEndSec !== null && seedTrimEndSec > seedTrimStartSec) {
      const end = Math.min(seedTrimEndSec, hookSafeDuration);
      setTrimEndSec(end);
      setTrimStartSec(Math.max(0, Math.min(seedTrimStartSec, end - MIN_TRIM_GAP_SEC)));
      return;
    }

    setTrimEndSec(hookSafeDuration);
    setTrimStartSec(0);
  }, [hookSafeDuration, seedTrimStartSec, seedTrimEndSec]);

  const trimVisual = useMemo(() => {
    if (hookSafeDuration <= 0) {
      return { start: 0, end: 0 };
    }

    const end = trimEndSec <= 0 ? hookSafeDuration : Math.min(trimEndSec, hookSafeDuration);
    const start = Math.max(0, Math.min(trimStartSec, end - MIN_TRIM_GAP_SEC));
    return { start, end };
  }, [hookSafeDuration, trimEndSec, trimStartSec]);

  const durationRef = useRef(duration);
  durationRef.current = duration;
  const onTrimRangeChangeRef = useRef(onTrimRangeChange);
  onTrimRangeChangeRef.current = onTrimRangeChange;

  trimStartRef.current = trimStartSec;
  trimEndRef.current = trimEndSec;

  const propClamp = playbackClamp;
  const propClampActive =
    propClamp !== null &&
    propClamp !== undefined &&
    Number.isFinite(propClamp.startSec) &&
    Number.isFinite(propClamp.endSec) &&
    propClamp.endSec > propClamp.startSec + MIN_TRIM_GAP_SEC * 0.5;

  let clampedEndFromDuration = 0;
  if (propClampActive) {
    clampedEndFromDuration = hookSafeDuration > 0 ? Math.min(propClamp.endSec, hookSafeDuration) : propClamp.endSec;
  }

  let clampedStartFromDuration = 0;
  if (propClampActive) {
    clampedStartFromDuration =
      hookSafeDuration > 0
        ? Math.max(0, Math.min(propClamp.startSec, clampedEndFromDuration - MIN_TRIM_GAP_SEC))
        : Math.max(0, Math.min(propClamp.startSec, propClamp.endSec - MIN_TRIM_GAP_SEC));
  }

  trimPlaybackRef.current = propClampActive
    ? {
        enabled: true,
        start: hookSafeDuration > 0 ? clampedStartFromDuration : propClamp.startSec,
        end: hookSafeDuration > 0 ? clampedEndFromDuration : propClamp.endSec
      }
    : {
        enabled: Boolean(
          (showTrimCurtains || (seedTrimStartSec !== null && seedTrimEndSec !== null)) &&
            hookSafeDuration > 0 &&
            trimVisual.end - trimVisual.start >= MIN_TRIM_GAP_SEC * 0.5
        ),
        start: trimVisual.start,
        end: trimVisual.end
      };

  /**
   * Trim editing lives directly on the scrubber as a two-thumb range slider (MUI `Slider` with an
   * array value) instead of draggable curtains over the picture — the picture stays clean, and the
   * range slider gets native track/rail contrast, `disableSwap`, and value labels for free.
   */
  const handleTrimRangeSliderChange = useCallback(
    (_event: Event, value: number | number[], activeThumb: number) => {
      if (!Array.isArray(value) || hookSafeDuration <= 0) {
        return;
      }

      const end = Math.min(hookSafeDuration, Math.max(value[1] ?? hookSafeDuration, (value[0] ?? 0) + MIN_TRIM_GAP_SEC));
      const start = Math.max(0, Math.min(value[0] ?? 0, end - MIN_TRIM_GAP_SEC));
      trimStartRef.current = start;
      trimEndRef.current = end;
      setTrimStartSec(start);
      setTrimEndSec(end);

      const p = playerRef.current;
      if (p) {
        if (preTrimDragTimeRef.current === null) {
          const now = p.currentTime();
          preTrimDragTimeRef.current = typeof now === "number" && Number.isFinite(now) ? now : 0;
        }

        const t = activeThumb === 0 ? start : end;
        p.currentTime(t);
        setCurrentTime(t);
      }
    },
    [hookSafeDuration]
  );

  const handleTrimRangeSliderCommit = useCallback(() => {
    const dur = Number.isFinite(durationRef.current) && durationRef.current > 0 ? durationRef.current : 0;
    if (dur <= 0) {
      return;
    }

    const end = trimEndRef.current <= 0 ? dur : Math.min(trimEndRef.current, dur);
    const start = Math.max(0, Math.min(trimStartRef.current, end - MIN_TRIM_GAP_SEC));

    const restoreTime = preTrimDragTimeRef.current;
    preTrimDragTimeRef.current = null;
    const p = playerRef.current;
    if (p && restoreTime !== null) {
      const t = Math.min(Math.max(restoreTime, start), end);
      p.currentTime(t);
      setCurrentTime(t);
    }

    onTrimRangeChangeRef.current?.({ startSec: start, endSec: end });
  }, []);

  const useInternalFetch = Boolean(!srcProp && projectId && videoId && fetchEnabled);
  const fetchProjectId = useInternalFetch ? (projectId ?? null) : null;
  const fetchVideoId = useInternalFetch ? (videoId ?? null) : null;
  const { data: videoData, isPending: videoLoading, isError: videoError } = useVideoEntity(fetchProjectId, fetchVideoId);

  const effectiveSrc = (typeof srcProp === "string" && srcProp.length > 0 ? srcProp : null) ?? videoData?.url ?? "";
  const uploadImage = useUploadImage();

  /** Keep latest src/poster/type for Video.js init without re-disposing the player when only the URL changes. */
  const effectiveSrcRef = useRef(effectiveSrc);
  const posterRef = useRef(poster);
  const sourceMimeTypeRef = useRef(sourceMimeType);
  effectiveSrcRef.current = effectiveSrc;
  posterRef.current = poster;
  sourceMimeTypeRef.current = sourceMimeType;

  const prevEffectiveSrcRef = useRef<string | null>(null);
  useEffect(() => {
    const prev = prevEffectiveSrcRef.current;
    prevEffectiveSrcRef.current = effectiveSrc;
    if (prev === null) {
      return;
    }

    if (effectiveSrc === prev) {
      return;
    }

    setTrimStartSec(0);
    setTrimEndSec(0);
  }, [effectiveSrc]);

  const handleCaptureFrame = useCallback(async () => {
    const player = playerRef.current;
    const video = getHtmlVideo(player);
    const canvas = canvasRef.current;
    if (!projectId) {
      return;
    }

    if (!video || !canvas || video.readyState < 2) {
      toastError("Video not ready. Pause or seek to a frame first");
      return;
    }

    const generalError = "Could not capture frame.";
    setCaptureFrameLoading(true);
    // Let React paint LoadingDialog before canvas work (drawImage / toBlob).
    await new Promise<void>((resolve) => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          resolve();
        });
      });
    });

    try {
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        toastError(generalError);
        return;
      }

      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      try {
        ctx.drawImage(video, 0, 0);
      } catch {
        toastError(generalError);
        return;
      }

      let blob: Blob | null = null;
      try {
        blob = await new Promise<Blob | null>((resolve, reject) => {
          try {
            canvas.toBlob((b) => resolve(b), "image/png", 0.95);
          } catch (syncErr) {
            reject(syncErr);
          }
        });
      } catch (e) {
        console.error(e);
        toastError(generalError);
        return;
      }

      if (!blob) {
        toastError(generalError);
        return;
      }

      const file = new File([blob], captureFrameFilename(effectiveSrc, videoId ?? null), {
        type: "image/png"
      });
      try {
        const { imageId } = await uploadImage.mutateAsync({ projectId, file });
        onCaptureFrame?.(imageId);
        toastSuccess("Frame attached to Description reference images");
      } catch (err) {
        const message = err instanceof Error ? err.message : "Upload failed";
        toastError(message);
      }
    } finally {
      setCaptureFrameLoading(false);
    }
  }, [effectiveSrc, onCaptureFrame, projectId, uploadImage, videoId]);

  const getTrimRange = useCallback((): { readonly startSec: number; readonly endSec: number } | null => {
    const dur = durationRef.current;
    const hookSafe = Number.isFinite(dur) && dur > 0 ? dur : 0;
    if (hookSafe <= 0) {
      return null;
    }

    const end = trimEndRef.current <= 0 ? hookSafe : Math.min(trimEndRef.current, hookSafe);
    const start = Math.max(0, Math.min(trimStartRef.current, end - MIN_TRIM_GAP_SEC));
    return { startSec: start, endSec: end };
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      captureFrame: handleCaptureFrame,
      getTrimRange
    }),
    [getTrimRange, handleCaptureFrame]
  );

  useEffect(() => {
    segmentEndNotifiedRef.current = false;
  }, [effectiveSrc, playbackClamp?.startSec, playbackClamp?.endSec]);

  const autoPlayRef = useRef(autoPlay);
  autoPlayRef.current = autoPlay;

  /** Match letterboxed video width; use 16:9 until intrinsic dimensions exist (avoids full-width control bar flash). */
  const updateControlBarWidth = useCallback(() => {
    const host = getVideoIngestHost(videoElRef.current);
    if (!host) {
      setControlBarWidthPx(null);
      return;
    }

    const W = host.clientWidth;
    const H = host.clientHeight;
    if (W <= 0 || H <= 0) {
      return;
    }

    const video = getHtmlVideo(playerRef.current);
    const vw = video?.videoWidth ?? 0;
    const vh = video?.videoHeight ?? 0;
    const videoAspect = vw > 0 && vh > 0 ? vw / vh : 16 / 9;

    const hostAspect = W / H;
    const displayedWidth = videoAspect > hostAspect ? W : H * videoAspect;
    setControlBarWidthPx(Math.round(displayedWidth));
  }, []);

  const updateFitContentHeight = useCallback(() => {
    if (!fitContentHeight) {
      return;
    }

    const host = getVideoIngestHost(videoElRef.current);
    if (!host) {
      return;
    }

    const W = host.clientWidth;
    if (W <= 0) {
      return;
    }

    const video = getHtmlVideo(playerRef.current);
    const vw = video?.videoWidth ?? 0;
    const vh = video?.videoHeight ?? 0;

    const vhAvail = globalThis.window === undefined ? 900 : (globalThis.window.visualViewport?.height ?? globalThis.window.innerHeight);
    const viewportCap = Math.floor(vhAvail * 0.92);
    const slotCap = typeof fitMaxHeightPx === "number" && fitMaxHeightPx > 0 ? Math.floor(fitMaxHeightPx) : viewportCap;
    const capPx = Math.min(viewportCap, slotCap);

    if (vw <= 0 || vh <= 0) {
      setFitHostHeightPx(Math.min(Math.round((W * 9) / 16), capPx));
      return;
    }

    const natural = (W * vh) / vw;
    setFitHostHeightPx(Math.min(Math.round(natural), capPx));
  }, [fitContentHeight, fitMaxHeightPx]);

  /**
   * Video.js must own `className` on the `data-vjs-player` node. MUI `Box` overwrites `className`
   * every render, stripping `video-js` / `vjs-controls-disabled` and breaking the big play button.
   */
  const vjsIngestHostStyle = useMemo((): CSSProperties => {
    const base: CSSProperties = {
      position: "relative",
      width: "100%",
      minHeight: 0,
      alignSelf: "stretch",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      overflow: "hidden",
      boxSizing: "border-box"
    };
    if (fitContentHeight) {
      return { ...base, flex: "0 0 auto", height: "100%" };
    }

    return { ...base, flex: 1, minHeight: 0, height: "100%" };
  }, [fitContentHeight]);

  useEffect(() => {
    let cancelled = false;
    let rafId = 0;
    let player: VideoJsPlayer | null = null;

    const initWhenConnected = () => {
      if (cancelled) {
        return;
      }

      const el = videoElRef.current;
      if (!el) {
        return;
      }

      // Vite HMR / Strict Mode can run this effect before the <video> is in the document.
      if (!el.isConnected) {
        rafId = globalThis.requestAnimationFrame(initWhenConnected);
        return;
      }

      const instance = videojs(
        el,
        {
          controls: false,
          controlBar: false,
          preload: "metadata",
          playsinline: playsInline,
          bigPlayButton: true,
          // `fill` uses vjs-fill and can crop the picture; disable when we size the host to aspect ratio.
          fill: !fitContentHeight,
          responsive: false,
          fluid: false,
          autoplay: autoPlay,
          disablePictureInPicture,
          plugins: {
            [BIG_PLAY_BUTTON_PLUGIN]: {}
          },
          html5: {
            vhs: {
              withCredentials: false
            }
          },
          ...(enableCrossOrigin ? { crossOrigin: "anonymous" as const } : {})
        },
        function (this: VideoJsPlayer) {
          if (cancelled) {
            return;
          }

          const v = getHtmlVideo(this);
          if (v && disablePictureInPicture) {
            v.disablePictureInPicture = true;
          }

          const url = effectiveSrcRef.current;
          if (url.length > 0) {
            const type = sourceMimeTypeRef.current ?? inferVideoMimeTypeFromUrl(url);
            this.src([{ src: url, type }]);
            const po = posterRef.current;
            if (po !== undefined && po.length > 0) {
              this.poster(po);
            } else {
              this.poster("");
            }
          }
        }
      );

      player = instance;
      playerRef.current = instance;

      const onTime = () => {
        const tr = trimPlaybackRef.current;
        let t = instance.currentTime() ?? 0;
        if (tr.enabled && tr.end > tr.start + 0.05) {
          if (t < tr.start) {
            instance.currentTime(tr.start);
            t = tr.start;
          } else if (t > tr.end) {
            const wasPlaying = !instance.paused();
            if (wasPlaying && playbackClampRef.current !== null && playbackClampRef.current !== undefined && !segmentEndNotifiedRef.current) {
              segmentEndNotifiedRef.current = true;
              onReachedPlaybackClampEndRef.current?.();
            }

            instance.pause();
            instance.currentTime(tr.end);
            t = tr.end;
          }
        }

        setCurrentTime(t);
      };

      const onDur = () => {
        setDuration(instance.duration() || 0);
      };

      const onLoadedMetadata = () => {
        onDur();
        updateControlBarWidth();
        updateFitContentHeight();
        const clamp = playbackClampRef.current;
        if (clamp !== null && clamp !== undefined && autoPlayRef.current) {
          const d = instance.duration() || 0;
          if (Number.isFinite(d) && d > 0) {
            const end = Math.min(clamp.endSec, d);
            const start = Math.max(0, Math.min(clamp.startSec, end - MIN_TRIM_GAP_SEC));
            instance.currentTime(start);
            segmentEndNotifiedRef.current = false;
            void instance.play();
          }
        }
      };

      const onPlay = () => {
        setPlaying(true);
        const tr = trimPlaybackRef.current;
        if (tr.enabled && tr.end > tr.start + 0.05) {
          const cur = instance.currentTime() ?? 0;
          if (cur < tr.start || cur >= tr.end - 0.05) {
            instance.currentTime(tr.start);
          }
        }
      };

      const onPause = () => {
        setPlaying(false);
      };

      const onVol = () => {
        setVolume(instance.volume() ?? 1);
        setMuted(instance.muted() ?? false);
      };

      const onRate = () => {
        setPlaybackRate(instance.playbackRate() ?? 1);
      };

      instance.on("timeupdate", onTime);
      instance.on("loadedmetadata", onLoadedMetadata);
      instance.on("durationchange", onDur);
      instance.on("play", onPlay);
      instance.on("pause", onPause);
      instance.on("volumechange", onVol);
      instance.on("ratechange", onRate);
      instance.on("ended", () => {
        onEndedRef.current?.();
      });
      const onLoadedData = () => {
        updateControlBarWidth();
        updateFitContentHeight();
      };

      instance.on("loadeddata", onLoadedData);

      // A successful resume after a retry (or just normal playback) means the transient error
      // is over - stop counting it against the retry budget for this source.
      instance.on("playing", () => {
        playbackRetryCountRef.current = 0;
      });

      instance.on("error", () => {
        const mediaError = instance.error();
        const isRetryableNetworkError = mediaError?.code === MEDIA_ERR_NETWORK;
        if (!isRetryableNetworkError || playbackRetryCountRef.current >= PLAYBACK_RETRY_MAX_ATTEMPTS) {
          onPlaybackErrorRef.current?.();
          return;
        }

        // Video.js/the browser does not retry a failed byte-range request on its own - it just
        // stalls playback where it stopped. Reload the same URL (a fresh presigned signature isn't
        // needed; the URL just expired-slash-failed transiently) and resume from where it stalled.
        const attempt = playbackRetryCountRef.current;
        playbackRetryCountRef.current += 1;
        const resumeTimeSec = instance.currentTime() ?? 0;
        const wasPlaying = !instance.paused();
        const delayMs = PLAYBACK_RETRY_BASE_DELAY_MS * 2 ** attempt;

        const resumePlayback = () => {
          instance.currentTime(resumeTimeSec);
          if (wasPlaying) {
            void instance.play();
          }
        };

        const reloadSourceAndResume = () => {
          playbackRetryTimeoutRef.current = null;
          const url = effectiveSrcRef.current;
          if (url.length === 0) {
            return;
          }

          const type = sourceMimeTypeRef.current ?? inferVideoMimeTypeFromUrl(url);
          instance.src([{ src: url, type }]);
          instance.one("loadedmetadata", resumePlayback);
        };

        if (playbackRetryTimeoutRef.current !== null) {
          clearTimeout(playbackRetryTimeoutRef.current);
        }

        playbackRetryTimeoutRef.current = setTimeout(reloadSourceAndResume, delayMs);
      });

      setVolume(instance.volume() ?? 1);
      setMuted(instance.muted() ?? false);
      setPlaybackRate(instance.playbackRate() ?? 1);
    };

    // Re-run when `effectiveSrc` changes so we init after `<video>` exists (internal fetch: first run
    // often sees no element while `videoLoading` shows the spinner).
    initWhenConnected();

    return () => {
      cancelled = true;
      globalThis.cancelAnimationFrame(rafId);
      if (playbackRetryTimeoutRef.current !== null) {
        clearTimeout(playbackRetryTimeoutRef.current);
        playbackRetryTimeoutRef.current = null;
      }

      playbackRetryCountRef.current = 0;
      if (player !== null) {
        player.dispose();
        playerRef.current = null;
      }
    };
  }, [effectiveSrc, playsInline, autoPlay, disablePictureInPicture, updateControlBarWidth, updateFitContentHeight, fitContentHeight, enableCrossOrigin]);

  useEffect(() => {
    const host = getVideoIngestHost(videoElRef.current);
    if (!host) {
      return;
    }

    const ro = new ResizeObserver(() => {
      updateControlBarWidth();
      updateFitContentHeight();
    });
    ro.observe(host);
    return () => {
      ro.disconnect();
    };
  }, [effectiveSrc, updateControlBarWidth, updateFitContentHeight]);

  useEffect(() => {
    setFitHostHeightPx(null);
  }, [effectiveSrc]);

  /** Measure control strip width before paint so the bar never briefly spans the full stage (e.g. MainSpace). */
  useLayoutEffect(() => {
    if (!effectiveSrc) {
      return;
    }

    updateControlBarWidth();
    updateFitContentHeight();
  }, [effectiveSrc, updateControlBarWidth, updateFitContentHeight]);

  useEffect(() => {
    if (!fitContentHeight) {
      return;
    }

    const onResize = () => {
      updateFitContentHeight();
    };

    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [fitContentHeight, updateFitContentHeight]);

  useEffect(() => {
    const onFullscreenChange = () => {
      setIsPlayerFullscreen(document.fullscreenElement === rootRef.current);
    };

    document.addEventListener("fullscreenchange", onFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", onFullscreenChange);
  }, []);

  useEffect(() => {
    const player = playerRef.current;
    if (!player || !effectiveSrc) {
      return;
    }

    const type = sourceMimeType ?? inferVideoMimeTypeFromUrl(effectiveSrc);

    player.ready(() => {
      player.src([{ src: effectiveSrc, type }]);
    });
  }, [effectiveSrc, sourceMimeType]);

  useEffect(() => {
    const player = playerRef.current;
    if (!player) {
      return;
    }

    player.ready(() => {
      if (poster !== undefined && poster.length > 0) {
        player.poster(poster);
      } else {
        player.poster("");
      }
    });
  }, [poster]);

  const togglePlay = useCallback(() => {
    const p = playerRef.current;
    if (!p) {
      return;
    }

    const isPaused = p.paused();
    if (isPaused === false) {
      p.pause();
    } else {
      void p.play();
    }
  }, []);

  const seekTo = useCallback((_: Event, value: number | number[]) => {
    const p = playerRef.current;
    if (!p) {
      return;
    }

    const raw = Array.isArray(value) ? value[0] : value;
    const t = typeof raw === "number" && Number.isFinite(raw) ? raw : 0;
    p.currentTime(t);
    setCurrentTime(t);
  }, []);

  const seekClampedToTrim = useCallback((raw: number) => {
    const p = playerRef.current;
    if (!p || !Number.isFinite(raw)) {
      return;
    }

    const end = trimEndRef.current > 0 ? trimEndRef.current : raw;
    const t = Math.min(Math.max(raw, trimStartRef.current), end);
    p.currentTime(t);
    setCurrentTime(t);
  }, []);

  const seekWithinTrim = useCallback(
    (_: Event, value: number | number[]) => {
      const raw = Array.isArray(value) ? value[0] : value;
      if (typeof raw === "number") {
        seekClampedToTrim(raw);
      }
    },
    [seekClampedToTrim]
  );

  const handleTrimBarPointerDown = useCallback(
    (e: ReactPointerEvent<HTMLElement>) => {
      if ((e.target as Element).closest(".MuiSlider-thumb")) {
        return;
      }

      const rect = e.currentTarget.getBoundingClientRect();
      const dur = Number.isFinite(durationRef.current) && durationRef.current > 0 ? durationRef.current : 0;
      if (rect.width <= 0 || dur <= 0) {
        return;
      }

      const fraction = Math.min(Math.max((e.clientX - rect.left) / rect.width, 0), 1);
      seekClampedToTrim(fraction * dur);
    },
    [seekClampedToTrim]
  );

  const setVol = useCallback((_: Event, value: number | number[]) => {
    const p = playerRef.current;
    if (!p) {
      return;
    }

    const raw = Array.isArray(value) ? value[0] : value;
    const vol = typeof raw === "number" && Number.isFinite(raw) ? raw : 0;
    p.volume(vol);
    if (vol > 0 && p.muted()) {
      p.muted(false);
    }

    setVolume(vol);
  }, []);

  const toggleMute = useCallback(() => {
    const p = playerRef.current;
    if (!p) {
      return;
    }

    p.muted(!p.muted());
  }, []);

  const requestFs = useCallback(() => {
    const n = rootRef.current;
    if (!n) {
      return;
    }

    if (document.fullscreenElement) {
      void document.exitFullscreen();
    } else {
      void n.requestFullscreen();
    }
  }, []);

  const applyPlaybackRate = useCallback((rate: number) => {
    const p = playerRef.current;
    if (!p) {
      return;
    }

    p.playbackRate(rate);
    setPlaybackRate(rate);
    setRateMenuAnchor(null);
  }, []);

  /** `controls: false` disables Video.js tech click-to-play; handle bubbled clicks on the stage. */
  const onVideoStageClick = useCallback(
    (event: ReactMouseEvent<HTMLDivElement>) => {
      const el = event.target;
      if (!(el instanceof Element)) {
        return;
      }

      if (el.closest(".vjs-big-play-button")) {
        return;
      }

      if (el.closest(".vjs-modal-dialog")) {
        return;
      }

      if (el.closest("[data-video-player-controls]")) {
        return;
      }

      if (el.closest("button") || el.closest('[role="slider"]')) {
        return;
      }

      togglePlay();
    },
    [togglePlay]
  );

  if (useInternalFetch && videoLoading) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 200 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (useInternalFetch && (videoError || !effectiveSrc)) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="error">Video not found or failed to load.</Typography>
      </Box>
    );
  }

  if (!effectiveSrc) {
    return null;
  }

  const videoAreaSx: SxProps<Theme> = {
    position: "relative",
    ...(fitContentHeight
      ? {
          flex: "0 0 auto",
          minHeight: fitHostHeightPx === null ? 200 : 0,
          ...(fitHostHeightPx === null ? {} : { height: fitHostHeightPx })
        }
      : {
          flex: 1,
          minHeight: 0,
          height: "100%",
          alignSelf: "stretch"
        }),
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
    bgcolor: "common.black",
    ...sx
  };

  const outerSx: SxProps<Theme> = {
    alignSelf: "stretch",
    width: "100%",
    maxWidth: "100%",
    minHeight: 0,
    ...(fitContentHeight ? { flex: "0 0 auto" } : { flex: 1 }),
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    borderRadius: 1,
    bgcolor: "background.paper",
    ...containerSx
  };

  const safeDuration = Number.isFinite(duration) && duration > 0 ? duration : 0;
  const sliderMax = safeDuration > 0 ? safeDuration : 1;

  const glassBarSx: SxProps<Theme> = {
    display: "flex",
    flexDirection: "row",
    flexWrap: "nowrap",
    alignItems: "center",
    gap: 1.5,
    minHeight: 30,
    minWidth: 0,
    width: "100%",
    maxWidth: "100%",
    boxSizing: "border-box",
    px: { xs: 1, sm: 1.5 },
    color: "common.white",
    bgcolor: "rgba(0, 0, 0, 0.3)",
    backdropFilter: "none",
    WebkitBackdropFilter: "none",
    boxShadow: "none",
    overflow: "hidden"
  };

  const controlStripWidth =
    controlBarWidthPx !== null && controlBarWidthPx > 0
      ? { width: controlBarWidthPx, maxWidth: "100%" as const }
      : { width: "100%" as const, maxWidth: "100%" as const };

  const controlChromeVisible = controlBarWidthPx !== null && controlBarWidthPx > 0;

  const overlaySliderSx: SxProps<Theme> = {
    color: "common.white",
    "& .MuiSlider-thumb": { bgcolor: "common.white", width: 12, height: 12 },
    "& .MuiSlider-rail": { bgcolor: "rgba(255,255,255,0.25)" },
    "& .MuiSlider-track": { bgcolor: "common.white" }
  };

  /** Amber band painted on the normal scrubber's rail so a Cut trim stays visible even when not editing. */
  const hasPersistedTrim = !showTrimCurtains && hookSafeDuration > 0 && (trimVisual.start > 0.05 || trimVisual.end < hookSafeDuration - 0.05);
  const persistedTrimStartPct = hookSafeDuration > 0 ? (trimVisual.start / hookSafeDuration) * 100 : 0;
  const persistedTrimEndPct = hookSafeDuration > 0 ? (trimVisual.end / hookSafeDuration) * 100 : 100;
  const seekSliderSx: SxProps<Theme> = {
    flex: "1 1 0",
    minWidth: 48,
    ...overlaySliderSx,
    ...(hasPersistedTrim
      ? {
          "& .MuiSlider-rail": {
            opacity: 1,
            background: trimRailHighlightGradient(persistedTrimStartPct, persistedTrimEndPct)
          }
        }
      : {})
  };
  const trimRangeSliderSx: SxProps<Theme> = {
    flex: "1 1 0",
    minWidth: 48,
    color: "warning.main",
    pointerEvents: "none",
    "& .MuiSlider-thumb": { pointerEvents: "auto", bgcolor: "warning.main", width: 2, height: 8, borderRadius: 0.5 },
    "& .MuiSlider-rail": { bgcolor: "rgba(255,255,255,0.25)" },
    "& .MuiSlider-track": { bgcolor: "warning.main" },
    "& .MuiSlider-valueLabel": { bgcolor: "warning.dark", fontSize: 11, fontVariantNumeric: "tabular-nums" }
  };
  const trimPlayheadSliderSx: SxProps<Theme> = {
    position: "absolute",
    left: 0,
    right: 0,
    top: "50%",
    transform: "translateY(-50%)",
    zIndex: 1,
    pointerEvents: "none",
    color: "common.white",
    "& .MuiSlider-rail, & .MuiSlider-track": { display: "none" },
    "& .MuiSlider-thumb": { pointerEvents: "auto", bgcolor: "common.white", width: 12, height: 12, zIndex: 2 }
  };
  const timeLabelSx: SxProps<Theme> = { color: "rgba(255,255,255,0.85)", fontVariantNumeric: "tabular-nums", flexShrink: 0, fontWeight: 500 };

  return (
    <Box ref={rootRef} sx={outerSx}>
      <Box className="video-player-stage" sx={videoAreaSx} onClick={onVideoStageClick}>
        <div data-vjs-player style={vjsIngestHostStyle}>
          <video
            ref={videoElRef}
            className="video-js vjs-default-skin vjs-big-play-centered"
            playsInline={playsInline}
            {...(poster !== undefined && poster.length > 0 ? { poster } : {})}
          />
        </div>
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-end",
            alignItems: "center",
            pointerEvents: "none",
            zIndex: 15
          }}
        >
          <Box
            data-video-player-controls
            onClick={(e) => {
              e.stopPropagation();
            }}
            onKeyDown={(e) => {
              e.stopPropagation();
            }}
            sx={{
              pointerEvents: controlChromeVisible ? "auto" : "none",
              boxSizing: "border-box",
              ...controlStripWidth,
              opacity: controlChromeVisible ? 1 : 0,
              background: "linear-gradient(to top, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.012) 45%, transparent 100%)"
            }}
          >
            <Box sx={glassBarSx}>
              <IconButton
                type="button"
                size="small"
                onClick={togglePlay}
                aria-label={playing ? "Pause" : "Play"}
                sx={{ color: "common.white", flexShrink: 0 }}
              >
                {playing ? <PauseIcon /> : <PlayArrowIcon />}
              </IconButton>
              {showTrimCurtains ? (
                <>
                  <Typography variant="caption" noWrap sx={timeLabelSx}>
                    {formatMediaTimeSeconds(currentTime)}
                  </Typography>
                  <Box
                    onPointerDown={handleTrimBarPointerDown}
                    sx={{ position: "relative", flex: "1 1 0", minWidth: 48, display: "flex", alignItems: "center", cursor: "pointer" }}
                  >
                    <Slider
                      size="small"
                      value={[trimVisual.start, trimVisual.end]}
                      min={0}
                      max={sliderMax}
                      step={0.05}
                      disableSwap
                      valueLabelDisplay="auto"
                      valueLabelFormat={formatMediaTimeSeconds}
                      onChange={handleTrimRangeSliderChange}
                      onChangeCommitted={handleTrimRangeSliderCommit}
                      sx={trimRangeSliderSx}
                    />
                    <Slider
                      size="small"
                      aria-label="Playhead"
                      value={Math.min(Math.max(currentTime, trimVisual.start), trimVisual.end)}
                      min={0}
                      max={sliderMax}
                      step={0.05}
                      onChange={seekWithinTrim}
                      sx={trimPlayheadSliderSx}
                    />
                  </Box>
                  <Typography variant="caption" noWrap sx={timeLabelSx}>
                    {formatMediaTimeSeconds(safeDuration)}
                  </Typography>
                </>
              ) : (
                <>
                  <Slider
                    size="small"
                    value={safeDuration > 0 ? Math.min(currentTime, safeDuration) : 0}
                    min={0}
                    max={sliderMax}
                    step={0.1}
                    onChange={seekTo}
                    sx={seekSliderSx}
                  />
                  <Typography
                    variant="caption"
                    noWrap
                    sx={{ color: "rgba(255,255,255,0.85)", fontVariantNumeric: "tabular-nums", flexShrink: 0, fontWeight: 500 }}
                  >
                    {formatMediaTimeSeconds(currentTime)}
                    {" / "}
                    {formatMediaTimeSeconds(safeDuration)}
                  </Typography>
                </>
              )}
              <Tooltip arrow title="Playback speed">
                <IconButton
                  type="button"
                  size="small"
                  onClick={(e) => {
                    setRateMenuAnchor(e.currentTarget);
                  }}
                  aria-label={`Playback speed ${formatPlaybackRateLabel(playbackRate)}`}
                  aria-haspopup="true"
                  aria-expanded={rateMenuAnchor !== null}
                  sx={{
                    flexShrink: 0,
                    minWidth: 44,
                    borderRadius: 2,
                    color: "rgba(255, 255, 255, 0.85)",
                    fontSize: "inherit"
                  }}
                >
                  <Typography
                    variant="caption"
                    component="span"
                    noWrap
                    sx={{
                      color: "inherit",
                      fontVariantNumeric: "tabular-nums",
                      fontWeight: 500
                    }}
                  >
                    {formatPlaybackRateLabel(playbackRate)}
                  </Typography>
                </IconButton>
              </Tooltip>
              <Menu
                anchorEl={rateMenuAnchor}
                open={rateMenuAnchor !== null}
                onClose={() => {
                  setRateMenuAnchor(null);
                }}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
                transformOrigin={{ vertical: "bottom", horizontal: "center" }}
              >
                {PLAYBACK_RATES.map((r) => (
                  <MenuItem
                    key={r}
                    selected={Math.abs(playbackRate - r) < 0.01}
                    onClick={() => {
                      applyPlaybackRate(r);
                    }}
                  >
                    {r}
                  </MenuItem>
                ))}
              </Menu>
              <IconButton
                type="button"
                size="small"
                onClick={toggleMute}
                aria-label={muted ? "Unmute" : "Mute"}
                sx={{ color: "common.white", flexShrink: 0 }}
              >
                {muted || volume === 0 ? <VolumeOffIcon /> : <VolumeUpIcon />}
              </IconButton>
              <Slider
                size="small"
                value={muted ? 0 : volume}
                min={0}
                max={1}
                step={0.05}
                onChange={setVol}
                sx={{
                  width: 56,
                  flex: "0 0 56px",
                  ...overlaySliderSx
                }}
              />
              <Tooltip arrow title={isPlayerFullscreen ? "Exit fullscreen" : "Enter fullscreen"}>
                <IconButton
                  type="button"
                  size="small"
                  onClick={requestFs}
                  aria-label={isPlayerFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
                  sx={{ color: "common.white", flexShrink: 0 }}
                >
                  <FullscreenIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Box>
      </Box>
      <canvas ref={canvasRef} style={{ display: "none" }} aria-hidden />
      <LoadingDialog open={captureFrameLoading} label="Capturing frame…" />
    </Box>
  );
});

VideoPlayer.displayName = "VideoPlayer";

export default VideoPlayer;
